// Trying out a delivery plan against the drums that exist, without saving it.
//
// The server decides one packing — hardest item first — and that is a good
// default and a bad answer to insist on. Whether to spend a 10,000 m drum on a
// hundred coils or keep it whole for the 8,000 m unit is a commercial call, not
// an arithmetic one, and the planner is the one making it.
//
// So this is the same packing rule, in the browser, run on every click: you set
// the quantities, and it says at once what they cost and what they make
// impossible. Nothing is written until the button is pressed.
//
// VERBATIM MATCH with redis_dev/helpers/tokenFeasibility.js — the two are pinned
// against each other by tests/tokenPlannerParity.test.js. Change one, change
// both, or the preview starts promising what the save then refuses.

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Whole units of `lengthPerUnit` that can be cut from one piece. */
export function unitsFrom(free, lengthPerUnit) {
  const L = num(lengthPerUnit);
  if (L <= 0) return 0;
  return Math.floor(num(free) / L);
}

/**
 * Serve an explicit list of demands, in the order given.
 *
 * Smallest piece that fits first: long continuous lengths are the scarce thing
 * in a cable plant, and spending one on a unit a shorter piece could have
 * covered is how a plan that adds up on paper fails on the floor.
 *
 * @param demands [{ itemId, lengthPerUnit, units }] in serving order
 */
export function packItems({ pieces = [], demands = [] }) {
  const pool = pieces.map(piece => ({ piece, remaining: piece.free }));

  const results = demands.map(d => {
    const L = num(d.lengthPerUnit);
    const need = Math.max(0, Math.floor(num(d.units)));
    const candidates = [];
    let got = 0;

    if (L > 0 && need > 0) {
      pool.sort((a, b) => a.remaining - b.remaining);
      for (const slot of pool) {
        if (got >= need) break;
        if (slot.piece.claimedBy && slot.piece.claimedBy !== d.itemId) continue;
        const can = unitsFrom(slot.remaining, L);
        if (can <= 0) continue;
        const take = Math.min(can, need - got);
        slot.remaining -= take * L;
        got += take;
        candidates.push({ ...slot.piece, unitsFromThisPiece: take, usedMeters: take * L });
      }
    }

    return {
      itemId: d.itemId,
      lengthPerUnit: L,
      unitsWanted: need,
      unitsFeasible: got,
      unitsShort: Math.max(0, need - got),
      metersUsed: got * L,
      candidates
    };
  });

  return {
    results,
    leftover: pool
      .filter(s => s.remaining > 0)
      .map(s => ({ ...s.piece, free: s.remaining }))
  };
}

/**
 * The most units of one item the pieces could yield if nothing else competed.
 * The ceiling on a manual choice: asking for more is asking for cable that has
 * no continuous length to come from.
 */
export function ceilingFor(pieces, lengthPerUnit, itemId = null) {
  return (pieces || []).reduce((s, p) => {
    if (p.claimedBy && p.claimedBy !== itemId) return s;
    return s + unitsFrom(p.free, lengthPerUnit);
  }, 0);
}

/**
 * What one set of choices does to everything else.
 *
 * The number that matters is not "can I have 100 coils" — it is "if I take 100
 * coils, the 2,000 m drum stops being possible". That consequence is invisible
 * until you do the packing, so it is computed here and shown next to the input
 * rather than discovered after saving.
 *
 * Two ceilings, and both bind. The factory's — how much continuous cable there
 * is — and the ORDER's: the plan asks for one 1,000 m drum, so one is all that
 * may be promised to it however many the cable would make. Ten thousand metres
 * of cable can be cut into ten such drums and nine of them belong to nobody.
 *
 * @param items   the delivery plan, in its own order. `cap` is what the order
 *                still has room for — ordered, less made, less already promised.
 * @param chosen  { itemId: units } — what the planner is asking for
 */
export function previewChoice({ pieces = [], items = [], chosen = {} }) {
  const capOf = it =>
    it.cap == null || !Number.isFinite(Number(it.cap))
      ? Infinity
      : Math.max(0, Number(it.cap));

  const demands = items.map(it => ({
    itemId: it.itemId,
    lengthPerUnit: it.lengthPerUnit,
    units: Math.min(num(chosen[it.itemId]), capOf(it))
  }));

  const { results, leftover } = packItems({ pieces, demands });
  const byItem = new Map(results.map(r => [String(r.itemId), r]));

  // Two numbers, and the gap between them IS the consequence of the choice.
  //
  //   maxAlone — what this item could have if it were the only one asking
  //   headroom — what it can still have now that the others have been chosen
  //
  // Take a hundred coils out of a ten thousand metre drum and the 2,000 m unit
  // goes from five possible to none. Showing only the first hides what the
  // choice cost; showing only the second hides that there was ever a choice.
  const headroom = new Map();
  const maxAlone = new Map();
  for (const it of items) {
    const cap = capOf(it);
    const others = demands.filter(d => String(d.itemId) !== String(it.itemId));
    const { leftover: spare } = packItems({ pieces, demands: others });
    headroom.set(
      String(it.itemId),
      Math.min(ceilingFor(spare, it.lengthPerUnit, it.itemId), cap)
    );
    maxAlone.set(
      String(it.itemId),
      Math.min(ceilingFor(pieces, it.lengthPerUnit, it.itemId), cap)
    );
  }

  return {
    results,
    leftover,
    byItem,
    headroom,
    maxAlone,
    metersUsed: results.reduce((s, r) => s + r.metersUsed, 0),
    metersLeft: leftover.reduce((s, p) => s + p.free, 0),
    // a choice that cannot be served in full is the planner asking for cable
    // that is not there — worth saying before the button is pressed
    overreach: results.filter(r => r.unitsShort > 0),
    // and one the ORDER has no room for, which the server would refuse: the
    // preview must never offer what the save then rejects
    overCap: items
      .filter(it => num(chosen[it.itemId]) > capOf(it))
      .map(it => ({ itemId: it.itemId, asked: num(chosen[it.itemId]), cap: capOf(it) }))
  };
}
